import React, { useEffect, useState, useRef } from 'react';
import { supabaseAdmin } from '../config/supabase';
import toast from 'react-hot-toast';
import './NewsManagement.css';
import './SegregacjaManagement.css';

const WASTE_CATEGORIES = [
  { value: 'plastic', name: 'Plastik', color: '#FFD700' },
  { value: 'bio', name: 'Bio', color: '#8B4513' },
  { value: 'glass', name: 'Szkło', color: '#34C759' },
  { value: 'paper', name: 'Papier', color: '#007AFF' },
  { value: 'mixed', name: 'Mieszane', color: '#8E8E93' },
  { value: 'pszok', name: 'Pszok', color: '#000000' },
];

const ACCEPTED_ICON_TYPES = 'image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp';

function isIconUrl(icon) {
  return typeof icon === 'string' && (icon.startsWith('http://') || icon.startsWith('https://'));
}

export default function SegregacjaManagement() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [uploadingIcon, setUploadingIcon] = useState(false);
  const iconInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    icon: '',
    category: 'bio',
    display_order: 0,
  });

  useEffect(() => {
    loadItems();
  }, []);

  const loadItems = async () => {
    try {
      const { data, error } = await supabaseAdmin
        .from('waste_segregation_items')
        .select('*')
        .order('display_order', { ascending: true });

      if (error) throw error;
      setItems(data || []);
    } catch (error) {
      toast.error('Błąd podczas ładowania przedmiotów');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Wprowadź nazwę przedmiotu');
      return;
    }

    if (!formData.icon || !formData.icon.trim()) {
      toast.error('Dodaj ikonę (obrazek JPG, PNG lub WEBP)');
      return;
    }

    try {
      if (editingItem) {
        // Aktualizacja istniejącego przedmiotu
        const { error } = await supabaseAdmin
          .from('waste_segregation_items')
          .update({
            name: formData.name.trim(),
            icon: formData.icon,
            category: formData.category,
            display_order: formData.display_order || 0,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editingItem.id);

        if (error) throw error;
        toast.success('Przedmiot zaktualizowany');
      } else {
        // Dodanie nowego przedmiotu
        const { error } = await supabaseAdmin
          .from('waste_segregation_items')
          .insert({
            name: formData.name.trim(),
            icon: formData.icon,
            category: formData.category,
            display_order: formData.display_order || 0,
          });

        if (error) throw error;
        toast.success('Przedmiot dodany');
      }

      setShowModal(false);
      setEditingItem(null);
      setFormData({
        name: '',
        icon: '',
        category: 'bio',
        display_order: 0,
      });
      loadItems();
    } catch (error) {
      toast.error(error.message || 'Błąd podczas zapisywania przedmiotu');
      console.error(error);
    }
  };

  const handleEditClick = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      icon: item.icon,
      category: item.category,
      display_order: item.display_order || 0,
    });
    setShowModal(true);
  };

  const handleDeleteClick = async (item) => {
    if (!window.confirm(`Czy na pewno chcesz usunąć przedmiot "${item.name}"?`)) {
      return;
    }

    try {
      const { error } = await supabaseAdmin
        .from('waste_segregation_items')
        .delete()
        .eq('id', item.id);

      if (error) throw error;
      toast.success('Przedmiot usunięty');
      loadItems();
    } catch (error) {
      toast.error('Błąd podczas usuwania przedmiotu');
      console.error(error);
    }
  };

  const uploadIconFile = async (file) => {
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['jpg', 'jpeg', 'png', 'webp'].includes(ext)) {
      throw new Error('Dozwolone formaty: JPG, PNG, WEBP');
    }
    const fileName = `segregation-icons/${Date.now()}-${Math.random().toString(36).slice(2, 11)}.${ext}`;
    const { error: uploadError } = await supabaseAdmin.storage
      .from('app-assets')
      .upload(fileName, file, { contentType: file.type, upsert: false });
    if (uploadError) throw uploadError;
    const { data: { publicUrl } } = supabaseAdmin.storage.from('app-assets').getPublicUrl(fileName);
    return publicUrl;
  };

  const handleIconFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingIcon(true);
    try {
      const url = await uploadIconFile(file);
      setFormData((prev) => ({ ...prev, icon: url }));
      toast.success('Ikona przesłana');
    } catch (err) {
      toast.error(err.message || 'Błąd przesyłania ikony');
      console.error(err);
    } finally {
      setUploadingIcon(false);
      e.target.value = '';
    }
  };

  const handleNewClick = () => {
    setEditingItem(null);
    setFormData({
      name: '',
      icon: '',
      category: 'bio',
      display_order: items.length > 0 ? Math.max(...items.map(i => i.display_order || 0)) + 1 : 0,
    });
    setShowModal(true);
  };

  const getCategoryInfo = (categoryValue) => {
    return WASTE_CATEGORIES.find(c => c.value === categoryValue) || WASTE_CATEGORIES[0];
  };

  if (loading) {
    return (
      <div className="management-container">
        <div className="loading">Ładowanie...</div>
      </div>
    );
  }

  return (
    <div className="management-container">
      <div className="management-header">
        <h2>Zarządzanie Segregacją</h2>
        <button className="btn btn-primary" onClick={handleNewClick}>
          + Dodaj przedmiot
        </button>
      </div>

      <div className="items-grid">
        {items.map((item) => {
          const categoryInfo = getCategoryInfo(item.category);
          return (
            <div key={item.id} className="item-card">
              <div className="item-icon-large">
                {isIconUrl(item.icon) ? (
                  <img src={item.icon} alt="" className="item-icon-img" />
                ) : (
                  <span>{item.icon || '—'}</span>
                )}
              </div>
              <div className="item-details">
                <h3>{item.name}</h3>
                <div className="item-category">
                  <span
                    className="category-badge"
                    style={{ backgroundColor: categoryInfo.color + '20', color: categoryInfo.color }}
                  >
                    {categoryInfo.name}
                  </span>
                </div>
                <div className="item-actions">
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => handleEditClick(item)}
                  >
                    Edytuj
                  </button>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => handleDeleteClick(item)}
                  >
                    Usuń
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {items.length === 0 && (
        <div className="empty-state">
          <p>Brak przedmiotów. Dodaj pierwszy przedmiot, aby rozpocząć.</p>
        </div>
      )}

      {/* Modal dodawania/edycji */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingItem ? 'Edytuj przedmiot' : 'Dodaj nowy przedmiot'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Nazwa przedmiotu *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="np. JABŁKO OWOCE I WARZYWA"
                  required
                />
              </div>

              <div className="form-group">
                <label>Ikona (obrazek JPG, PNG lub WEBP) *</label>
                <div className="icon-upload-area">
                  <div className="icon-preview">
                    {formData.icon ? (
                      isIconUrl(formData.icon) ? (
                        <img src={formData.icon} alt="Ikona" className="icon-preview-img" />
                      ) : (
                        <span className="icon-preview-emoji">{formData.icon}</span>
                      )
                    ) : (
                      <span className="icon-preview-placeholder">Brak ikony</span>
                    )}
                  </div>
                  <input
                    ref={iconInputRef}
                    type="file"
                    accept={ACCEPTED_ICON_TYPES}
                    onChange={handleIconFileChange}
                    style={{ display: 'none' }}
                  />
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => iconInputRef.current?.click()}
                    disabled={uploadingIcon}
                  >
                    {uploadingIcon ? 'Przesyłanie...' : 'Wybierz plik ikony'}
                  </button>
                  {formData.icon && (
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary"
                      onClick={() => setFormData((prev) => ({ ...prev, icon: '' }))}
                      style={{ marginTop: 8 }}
                    >
                      Usuń ikonę
                    </button>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Kategoria *</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  required
                >
                  {WASTE_CATEGORIES.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Kolejność wyświetlania</label>
                <input
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                  min="0"
                />
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Anuluj
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingItem ? 'Zaktualizuj' : 'Dodaj'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

